import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import apiClient from '../../magpiie-client/client';
import type { RootState } from '../store';

/**
 * Fetch current subscription for an organization.
 * Uses condition to prevent duplicate fetches (same pattern as fetchChannelsForOrg).
 */
export const fetchCurrentSubscription = createAsyncThunk(
  'subscription/fetchCurrentSubscription',
  async (orgId: number, { rejectWithValue }) => {
    const { data, error } = await apiClient.GET(
      '/v2/payments/organizations/{orgId}/current-subscription',
      {
        params: { path: { orgId: String(orgId) } },
      }
    );
    if (error) {
      return rejectWithValue(
        (error as any).message || 'Failed to fetch subscription'
      );
    }
    return { orgId, subscription: data ?? null };
  },
  {
    condition: (orgId, { getState }) => {
      const { subscription } = getState() as RootState;
      if (
        subscription.isSubscriptionLoading ||
        subscription.fetchedByOrg[String(orgId)]
      ) {
        return false;
      }
      return true;
    },
  }
);

/**
 * Fetch invoices for an organization
 */
export const fetchInvoices = createAsyncThunk(
  'subscription/fetchInvoices',
  async (orgId: number, { rejectWithValue }) => {
    const { data, error } = await apiClient.GET(
      '/v2/payments/organizations/{orgId}/invoices',
      {
        params: { path: { orgId: String(orgId) } },
      }
    );
    if (error) {
      return rejectWithValue(
        (error as any).message || 'Failed to fetch invoices'
      );
    }
    return data || [];
  }
);

/**
 * Cancel a subscription
 */
export const cancelSubscription = createAsyncThunk(
  'subscription/cancelSubscription',
  async (
    {
      orgId,
      provider,
      subscriptionGatewayId,
    }: { orgId: number; provider: string; subscriptionGatewayId: string },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.POST(
      '/v2/payments/{orgId}/{provider}/cancel/{id}',
      {
        params: {
          path: { orgId, provider, id: subscriptionGatewayId },
        },
      }
    );
    if (error) {
      return rejectWithValue(
        (error as any).message || 'Failed to cancel subscription'
      );
    }
    return data;
  }
);

/**
 * Reset subscription fetched flag for an org (forces refetch).
 * Used after successful checkout to refresh subscription state.
 */
export const resetSubscriptionFetched = createAction<number>(
  'subscription/resetSubscriptionFetched'
);

/**
 * Fetch organization usage (members / channels / storage used vs. max) for
 * the active org. Shared across pages and limit-gate UI — cached per-org
 * with a `condition` guard mirroring `fetchCurrentSubscription`.
 */
export const fetchOrganizationUsage = createAsyncThunk(
  'subscription/fetchOrganizationUsage',
  async (orgId: number, { rejectWithValue }) => {
    const { data, error } = await apiClient.GET(
      '/v2/organizations/{orgId}/usage',
      {
        params: { path: { orgId } },
      }
    );
    if (error) {
      return rejectWithValue(
        (error as any).message || 'Failed to fetch organization usage'
      );
    }
    return { orgId, usage: data ?? null };
  },
  {
    condition: (orgId, { getState }) => {
      const { subscription } = getState() as RootState;
      if (
        subscription.isUsageLoading ||
        subscription.usageFetchedByOrg[String(orgId)]
      ) {
        return false;
      }
      return true;
    },
  }
);

/**
 * Invalidate cached usage for an org. Dispatch after any mutation that
 * changes the channel or member count (channel-add, member-invite,
 * channel-delete, member-remove) so the next `fetchOrganizationUsage`
 * actually hits the network instead of short-circuiting on `condition`.
 */
export const resetUsageFetched = createAction<number>(
  'subscription/resetUsageFetched'
);
